import React, { useContext, useEffect, useRef, useState } from "react"
import kopyLogo from "../assets/kopy-logo-square.png"
import ShopifyLogo from "../assets/supported-platforms/shopify-logo.png"
import { CoolButton } from "../components/CoolButton.jsx"
import { LanguageSelector } from "../components/LanguageSelector.jsx"
import { Loader } from "../components/LoadingToast.jsx"
import { OnboardingTimeline } from "../components/OnboardingTimeline.jsx"
import { TopMenu } from "../components/TopMenu.jsx"
import { GlobalStateContext } from "../components/providers/GlobalStateProvider.jsx"
import { useAuthenticatedFetch } from "../hooks/useAuthenticatedFetch.js"
import { useElementDimensions } from "../hooks/useElementDimensions.js"
import { useMediaQuery } from "../hooks/useMediaQuery.js"
import { useTranslation } from "react-i18next"
import posthog from "posthog-js"

const Authentication = ({ newUser = false }) => {
	const isMobile = useMediaQuery("(max-width: 500px)")
	const { right: containerRight, ref: containerRef } = useElementDimensions()
	const { t } = useTranslation()

	return (
		<div
			style={{
				width: "100%",
				height: "100%",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				padding: "0rem 1rem",
			}}
		>
			<TopMenu accountSelector storeSelector />

			<div
				style={{
					display: "flex",
					gap: "1rem",
					justifyContent: "center",
					alignItems: "center",
					position: "relative",
					width: "100%",
				}}
			>
				<div
					className="border shadow"
					ref={containerRef}
					style={{
						marginTop: "5rem",
						marginBottom: "5rem",
						maxWidth: isMobile ? "100%" : "480px",
						height: "auto",
						padding: "1.5rem",
						background: "#fff",
						borderRadius: "2rem",
						flex: "1",
						display: "flex",
						flexDirection: "column",
						transition: "height 0.5s ease",
					}}
				>
					<img
						style={{
							display: "block",
							height: "10rem",
							width: "100%",
							objectFit: "cover",
							margin: "auto",
							marginBottom: "1rem",
						}}
						src="https://kopy.b-cdn.net/connect1.webp"
						srcSet="https://kopy.b-cdn.net/connect2.webp 2x"
					/>

					<h1
						style={{
							fontSize: "1.6rem",
							fontWeight: "600",
							color: "#222",
							textAlign: "center",
						}}
					>
						{t("authentication.connect_your_shopify_store")}
					</h1>
					<p
						style={{
							fontSize: "0.8rem",
							fontWeight: "400",
							color: "#555",
							textAlign: "center",
							marginBottom: "3rem",
						}}
					>
						{t(
							"authentication.connect_your_shopify_store_to_kopy_to_start_using_the_app",
						)}
					</p>

					<AuthenticationComponent newUser={newUser} />
				</div>

				<div
					className="float-right"
					style={{
						left: `calc(${containerRight}px + 0.2rem)`,
						top: "7rem",
						paddingRight: "1rem",
					}}
				>
					<OnboardingTimeline step={3} newUser={newUser} />
					<style>{`
						.float-right {
							position: absolute;
							width: max-content;
						}

						@media (max-width: 900px) {
							.float-right {
								display: none;
							}
						}
						@media (max-width: 1200px) {
							.float-right {
								width: fit-content;
							}
						}
					`}</style>
				</div>
			</div>

			<div
				style={{
					position: "fixed",
					bottom: "2rem",
					left: "2rem",
					zIndex: "500",
				}}
			>
				<LanguageSelector />
			</div>
		</div>
	)
}

export const AuthenticationComponent = ({
	newUser = false,
	forceCreateNewApp = false,
}) => {
	const { t } = useTranslation()
	const [step, setStep] = useState(1)
	const [installTarget, setInstallTarget] = useState(null)
	const [connectError, setConnectError] = useState(null)

	const onAppReady = (target) => {
		setInstallTarget(target)
		setStep(2)
	}

	// The OAuth callback lands on / with ?connect_error=<reason> when an
	// install did not finish. Show it once, above whichever step renders.
	useEffect(() => {
		const params = new URLSearchParams(window.location.search)
		const reason = params.get("connect_error")
		if (!reason) return
		const key = `authentication.connect_error_${reason}`
		posthog?.capture("store_connect_error_shown", { reason })
		setConnectError(
			t(key, {
				defaultValue: t("authentication.connect_error_link_failed"),
			}),
		)
		params.delete("connect_error")
		const query = params.toString()
		window.history.replaceState(
			null,
			"",
			`${window.location.pathname}${query ? `?${query}` : ""}`,
		)
	}, [])

	const disconnectedIcon = (
		<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
			<path
				fill="currentColor"
				d="M43.634 4.366a1.25 1.25 0 0 1 0 1.768l-4.913 4.913a9.253 9.253 0 0 1-.744 12.244l-3.343 3.343a1.25 1.25 0 0 1-1.768 0l-11.5-11.5a1.25 1.25 0 0 1 0-1.768l3.343-3.343a9.25 9.25 0 0 1 12.244-.743l4.913-4.914a1.25 1.25 0 0 1 1.768 0M9.28 36.953l-4.914 4.913a1.25 1.25 0 0 0 1.768 1.768l4.913-4.913a9.253 9.253 0 0 0 12.244-.744l3.343-3.343a1.25 1.25 0 0 0 0-1.768L25.268 31.5l3.366-3.366a1.25 1.25 0 0 0-1.768-1.768L23.5 29.732L18.268 24.5l3.366-3.366a1.25 1.25 0 0 0-1.768-1.768L16.5 22.732l-1.366-1.366a1.25 1.25 0 0 0-1.768 0l-3.343 3.343a9.25 9.25 0 0 0-.743 12.244"
			/>
		</svg>
	)

	const connectedIcon = (
		<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
			<path
				fill="currentColor"
				d="M17.78 3.28a.75.75 0 0 0-1.06-1.06l-2.446 2.445a4.04 4.04 0 0 0-5.128.481l-.3.3a1.49 1.49 0 0 0 0 2.108l2.465 2.464a5.51 5.51 0 0 1 4.552-.848a4.04 4.04 0 0 0-.528-3.444zM7.554 8.846l2.464 2.465a5.51 5.51 0 0 0-.848 4.552a4.04 4.04 0 0 1-3.444-.528L3.28 17.78a.75.75 0 0 1-1.06-1.06l2.446-2.446a4.04 4.04 0 0 1 .48-5.128l.3-.3a1.49 1.49 0 0 1 2.108 0M19 14.5a4.5 4.5 0 1 1-9 0a4.5 4.5 0 0 1 9 0m-2.146-1.854a.5.5 0 0 0-.708 0L13.5 15.293l-.646-.647a.5.5 0 0 0-.708.708l1 1a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0 0-.708"
			/>
		</svg>
	)

	return (
		<div>
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					gap: "0.2rem",
					padding: "1rem 0rem",
					marginBottom: "1rem",
				}}
			>
				<div
					className="border shadow"
					style={{
						padding: "0.5rem",
						background:
							"linear-gradient(to right bottom, #eee 0%, #fff 70%)",
						borderRadius: "0.5rem",
					}}
				>
					<img
						style={{
							width: "3rem",
							height: "3rem",
							borderRadius: "0.5rem",
						}}
						src={kopyLogo}
					/>
				</div>

				<div
					style={{
						marginLeft: "0.2rem",
						width: "1.5rem",
						height: "1px",
						borderBottom: "1px dashed #aaa",
					}}
				/>

				<div
					style={{
						height: "1.5rem",
						width: "1.5rem",
						color: step === 1 ? "#777" : "#5271FF",
					}}
				>
					{step === 1 ? disconnectedIcon : connectedIcon}
				</div>

				<div
					style={{
						marginRight: "0.2rem",
						width: "1.5rem",
						height: "1px",
						borderBottom: "1px dashed #aaa",
					}}
				/>

				<div
					className="border shadow"
					style={{
						padding: "0.5rem",
						background:
							"linear-gradient(to right bottom, #eee 0%, #fff 70%)",
						borderRadius: "0.5rem",
					}}
				>
					<img
						src={ShopifyLogo}
						style={{ height: "3rem", width: "3rem" }}
					/>
				</div>
			</div>

			{connectError ? (
				<div
					style={{
						marginBottom: "1rem",
						padding: "0.6rem 0.8rem",
						borderRadius: "0.5rem",
						background: "#fff3f3",
						border: "1px solid #f5c2c2",
						color: "#a12020",
						fontSize: "0.75rem",
						fontWeight: "500",
						textAlign: "center",
					}}
				>
					{connectError}
				</div>
			) : (
				""
			)}

			{step === 1 ? (
				<Step1
					newUser={newUser}
					forceCreateNewApp={forceCreateNewApp}
					onAppReady={onAppReady}
				/>
			) : step === 2 ? (
				<Step2 installTarget={installTarget} />
			) : (
				""
			)}
		</div>
	)
}

const Step1 = ({ newUser, forceCreateNewApp, onAppReady }) => {
	const { state } = useContext(GlobalStateContext)
	const { t } = useTranslation()
	const authenticatedFetch = useAuthenticatedFetch()
	const store = state.shop

	const activeSubscriptionId = newUser
		? state.otherKopyBasicSubscriptions?.[0]?.id ||
			state.otherKopyProtectSubscriptions?.[0]?.id ||
			""
		: ""

	const [deviceCode, setDeviceCode] = useState(null)
	const [verificationUriComplete, setVerificationUriComplete] = useState(null)
	const [userCode, setUserCode] = useState(null)
	const [appManagementToken, setAppManagementToken] = useState(null)
	const [businessPlatformToken, setBusinessPlatformToken] = useState(null)
	const [organizations, setOrganizations] = useState([])
	const [accessibleShops, setAccessibleShops] = useState([])
	const [selectedShopUrl, setSelectedShopUrl] = useState(null)
	const [showOrganizations, setShowOrganizations] = useState(false)
	const [showShopSelector, setShowShopSelector] = useState(false)
	const [organizationIdSelected, setOrganizationIdSelected] = useState(null)
	const [error, setError] = useState(null)

	const [loadingInitKopyAuth, setLoadingInitKopyAuth] = useState(false)
	const [loadingGetStoreApps, setLoadingGetStoreApps] = useState(false)
	const [loadingPolling, setLoadingPolling] = useState(false)
	const [loadingCreateApp, setLoadingCreateApp] = useState(false)
	const [loadingGetOrganizations, setLoadingGetOrganizations] =
		useState(false)
	const [loadingSelectShop, setLoadingSelectShop] = useState(false)

	const getStoreApps = () => {
		return authenticatedFetch(
			`/api/auth/getStoreApps?store=${encodeURIComponent(store)}`,
		)
			.then((res) => res.json())
			.then((data) => {
				return data?.storeApps || []
			})
			.catch((err) => {
				console.error(err)
				return []
			})
	}

	const initKopyAuth = async () => {
		return authenticatedFetch("/api/auth/initKopyAuth")
			.then((res) => res.json())
			.then((data) => {
				return {
					deviceCode: data?.deviceCode || null,
					userCode: data?.userCode || null,
					verificationUriComplete:
						data?.verificationUriComplete || null,
				}
			})
			.catch((err) => {
				console.error(err)
				return {
					deviceCode: null,
					userCode: null,
					verificationUriComplete: null,
				}
			})
	}

	const goToInstallStep = (appId, storeOverride = store) => {
		onAppReady({
			appId,
			store: storeOverride || store,
			activeSubscriptionId,
		})
	}

	const pollAuthorization = async ({ deviceCode, attemptNumber = 1 }) => {
		if (!deviceCode) return

		const maxAttempts = 100

		const startPolling = () => {
			if (attemptNumber > maxAttempts) {
				return {
					status: "max_attempts_reached",
					appManagementToken: null,
					businessPlatformToken: null,
					error: null,
				}
			}

			return authenticatedFetch(
				`/api/auth/pollAuthorization?deviceCode=${encodeURIComponent(deviceCode)}`,
			)
				.then((res) => res.json())
				.then(async (data) => {
					return {
						status: data?.status,
						appManagementToken: data?.appManagementToken || null,
						businessPlatformToken:
							data?.businessPlatformToken || null,
						error: data?.error || null,
					}
				})
				.catch((err) => {
					console.error(err)
					return {
						status: "catched_error",
						appManagementToken: null,
						businessPlatformToken: null,
						error: err.message,
					}
				})
		}

		const { status, appManagementToken, businessPlatformToken } =
			await startPolling()

		if (status === "success") {
			if (!appManagementToken || !businessPlatformToken) {
				setError("Failed to get identity token")
				return
			}
			setAppManagementToken(appManagementToken)
			setLoadingPolling(false)
			getOrganizations({ businessPlatformToken, appManagementToken })
			return
		} else if (status === "authorization_pending") {
			await new Promise((resolve) => setTimeout(resolve, 2000))
			return await pollAuthorization({
				deviceCode,
				attemptNumber: attemptNumber + 1,
			})
		} else if (status === "slow_down") {
			await new Promise((resolve) => setTimeout(resolve, 5000))
			return await pollAuthorization({
				deviceCode,
				attemptNumber: attemptNumber + 1,
			})
		}

		setLoadingPolling(false)
		if (status === "catched_error") {
			setError("Unexpected error. Please reload the page and try again.")
		} else if (status === "max_attempts_reached") {
			setError(
				"Max attempts reached. Please reload the page and try again.",
			)
		} else {
			setError("Unexpected error. Please reload the page and try again.")
		}
	}

	const getOrganizations = async ({
		businessPlatformToken: bpToken,
		appManagementToken,
	}) => {
		setLoadingGetOrganizations(true)
		setBusinessPlatformToken(bpToken)
		await authenticatedFetch("/api/auth/getOrganizations", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				businessPlatformToken: bpToken,
				store,
			}),
		})
			.then((res) => res.json())
			.then((data) => {
				if (data?.storeOrganizationId) {
					createApp({
						organizationId: data.storeOrganizationId,
						appManagementToken,
						store: data.realShopUrl || store,
					})
				} else if (data?.accessibleShops?.length > 0) {
					// Show shop selector when no auto-match but shops available
					setAccessibleShops(data.accessibleShops)
					setOrganizations(data.organizations)
					setShowShopSelector(true)
				} else {
					setError(
						`No organization found for the store you are trying to connect to. Please log in to the Shopify account with access to ${store} and try again.`,
					)
				}
			})
			.catch((err) => {
				console.error(err)
				setError("Failed to get organizations")
			})
		setLoadingGetOrganizations(false)
	}

	const handleSelectShop = async () => {
		if (!selectedShopUrl || !businessPlatformToken) {
			setError("Please select your store to continue.")
			return
		}

		const shop = accessibleShops.find((s) => s.shopUrl === selectedShopUrl)
		if (!shop) {
			setError("Invalid shop selection.")
			return
		}

		setLoadingSelectShop(true)
		try {
			const response = await authenticatedFetch("/api/auth/selectShop", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					shopUrl: shop.shopUrl,
					organizationId: shop.organizationId,
					businessPlatformToken,
				}),
			}).then((res) => res.json())

			if (response.error) {
				setError(response.error)
				setLoadingSelectShop(false)
				return
			}

			await createApp({
				organizationId: shop.organizationId,
				appManagementToken,
				store: shop.shopUrl,
			})
			setAccessibleShops([])
			setShowShopSelector(false)
		} catch (err) {
			console.error(err)
			setError("Failed to select store.")
		}
		setLoadingSelectShop(false)
	}

	const createApp = async ({
		organizationId,
		appManagementToken,
		store: storeOverride,
	}) => {
		if (!organizationId || !appManagementToken) return
		let appId = null
		const effectiveStore = storeOverride || store

		setLoadingCreateApp(true)
		await authenticatedFetch("/api/auth/createApp", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				organizationId,
				appManagementToken,
				store: effectiveStore,
			}),
		})
			.then((res) => res.json())
			.then((data) => {
				appId = data?.appId || null
			})
			.catch((err) => {
				console.error(err)
			})
		setLoadingCreateApp(false)

		if (!appId) {
			setError("Failed to create app")
			return
		}

		goToInstallStep(appId, effectiveStore)
	}

	useEffect(async () => {
		if (!forceCreateNewApp) {
			setLoadingGetStoreApps(true)
			const storeApps = await getStoreApps()
			setLoadingGetStoreApps(false)

			if (storeApps[0]?.id) {
				goToInstallStep(storeApps[0].id)
				return
			}
		}

		// Get authorization URL and device code for polling
		setLoadingInitKopyAuth(true)
		const { userCode, verificationUriComplete, deviceCode } =
			await initKopyAuth()
		setLoadingInitKopyAuth(false)
		if (!deviceCode || !verificationUriComplete || !userCode) {
			setError(
				"Failed to initialize Authorization. Missing parameters returned.",
			)
			return
		}
		setUserCode(userCode)
		setVerificationUriComplete(verificationUriComplete)
		setDeviceCode(deviceCode)
	}, [])

	const loading =
		loadingCreateApp ||
		loadingGetStoreApps ||
		loadingInitKopyAuth ||
		loadingGetOrganizations ||
		loadingSelectShop

	if (showShopSelector && accessibleShops.length > 0) {
		return (
			<ShopSelector
				shops={accessibleShops}
				selectedShopUrl={selectedShopUrl}
				onShopSelect={(shopUrl) => setSelectedShopUrl(shopUrl)}
				onConfirm={handleSelectShop}
				loading={loadingSelectShop}
			/>
		)
	}

	if (showOrganizations && organizations.length > 0) {
		return (
			<OrganizationSelector
				organizations={organizations}
				organizationIdSelected={organizationIdSelected}
				onOrganizationSelect={(orgId) =>
					setOrganizationIdSelected(orgId)
				}
				onConfirm={() => {
					setShowOrganizations(false)
					createApp({
						organizationId: organizationIdSelected,
						appManagementToken,
					})
				}}
			/>
		)
	}

	if (showOrganizations && organizations.length === 0) {
		return (
			<div>
				<CoolButton
					fontSize="0.8rem"
					innerPadding={"0.5rem 2rem"}
					colored={true}
					onClick={() => {
						window.open(
							"https://dev.shopify.com/dashboard",
							"_blank",
						)
					}}
				>
					<p style={{ color: "#fff" }}>
						{t("authentication.continue")}
					</p>
				</CoolButton>
				<p
					style={{
						marginTop: "0.5rem",
						textAlign: "center",
						fontSize: "0.6rem",
						fontWeight: "500",
						color: "#555",
					}}
				>
					{t("authentication.no_organizations_found")}
				</p>
				<p
					style={{
						textAlign: "center",
						fontSize: "0.6rem",
						fontWeight: "500",
						color: "#555",
					}}
				>
					{t(
						"authentication.click_the_button_above_and_authorize_with_shopify",
					)}
				</p>
			</div>
		)
	}

	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
			}}
		>
			<CoolButton
				disabled={loading}
				fontSize="0.8rem"
				innerPadding={"0.5rem 2rem"}
				colored={true}
				onClick={() => {
					if (!verificationUriComplete) return
					window.open(verificationUriComplete, "_blank")
					setLoadingPolling(true)
					pollAuthorization({ deviceCode })
				}}
			>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						gap: "0.3rem",
					}}
				>
					{loading ? (
						<Loader size="0.9rem" color="#fff" />
					) : (
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="0.9rem"
							height="0.9rem"
							color="#fff"
							viewBox="0 0 24 24"
						>
							<g
								fill="none"
								stroke="currentColor"
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth="2"
							>
								<circle cx="10" cy="7" r="4" />
								<path d="M10.3 15H7a4 4 0 0 0-4 4v2m12-5.5V14a2 2 0 0 1 4 0v1.5" />
								<rect
									width="8"
									height="5"
									x="13"
									y="16"
									rx=".899"
								/>
							</g>
						</svg>
					)}
					<p
						style={{
							fontSize: "0.75rem",
							fontWeight: "500",
							color: "#fff",
						}}
					>
						{loadingGetOrganizations
							? t("authentication.loading_organizations")
							: loadingCreateApp
								? t("authentication.creating_app")
								: loadingGetStoreApps
									? t("authentication.initializing")
									: loadingInitKopyAuth
										? t("authentication.initializing")
										: t(
												"authentication.authorize_with_shopify",
											)}
					</p>
				</div>
			</CoolButton>

			{userCode && !loading && (
				<div style={{ marginTop: "0.3rem" }}>
					<p
						style={{
							textAlign: "center",
							fontSize: "0.6rem",
							fontWeight: "500",
							color: "#555",
						}}
					>
						{t(
							"authentication.confirm_the_code_with_the_one_shown_below",
						)}
					</p>
					<p
						style={{
							fontFamily: "monospace",
							textAlign: "center",
							fontSize: "0.85rem",
							fontWeight: "700",
							color: "#333",
						}}
					>
						{userCode}
					</p>
				</div>
			)}

			{error ? (
				<div
					style={{
						marginTop: "0.5rem",
						color: "red",
						fontSize: "0.8rem",
						fontWeight: "500",
					}}
				>
					{error}
				</div>
			) : (
				""
			)}
		</div>
	)
}

const OrganizationSelector = ({
	organizations,
	organizationIdSelected,
	onOrganizationSelect,
	onConfirm,
}) => {
	const { t } = useTranslation()

	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
			}}
		>
			<p
				style={{
					textAlign: "center",
					fontSize: "1.2rem",
					fontWeight: "700",
					color: "#333",
				}}
			>
				{t("authentication.choose_an_organization")}
			</p>
			<p
				style={{
					textAlign: "center",
					fontSize: "0.7rem",
					fontWeight: "500",
					color: "#555",
					marginBottom: "1rem",
				}}
			>
				{t(
					"authentication.the_app_will_be_created_in_the_organization_selected_below",
				)}
			</p>
			<div style={{ marginBottom: "0.5rem" }}>
				{organizations.map((organization) => (
					<div
						key={organization.id}
						className="shadow border"
						style={{
							padding: "0.3rem 0.8rem",
							borderRadius: "0.5rem",
							background:
								"linear-gradient(to right bottom, #eee 0%, #fff 90%)",
							color: "#444",
							marginBottom: "0.5rem",
							display: "flex",
							alignItems: "center",
							gap: "0.5rem",
							cursor: "pointer",
							minWidth: "12rem",
						}}
						onClick={() => onOrganizationSelect(organization.id)}
					>
						<svg
							style={{ height: "0.8rem", width: "0.8rem" }}
							viewBox="0 0 16 16"
						>
							<path
								fill="currentColor"
								d="M1.75 16A1.75 1.75 0 0 1 0 14.25V1.75C0 .784.784 0 1.75 0h8.5C11.216 0 12 .784 12 1.75v12.5q0 .127-.018.25h2.268a.25.25 0 0 0 .25-.25V8.285a.25.25 0 0 0-.111-.208l-1.055-.703a.749.749 0 1 1 .832-1.248l1.055.703c.487.325.779.871.779 1.456v5.965A1.75 1.75 0 0 1 14.25 16h-3.5a.8.8 0 0 1-.197-.026q-.148.026-.303.026h-3a.75.75 0 0 1-.75-.75V14h-1v1.25a.75.75 0 0 1-.75.75Zm-.25-1.75c0 .138.112.25.25.25H4v-1.25a.75.75 0 0 1 .75-.75h2.5a.75.75 0 0 1 .75.75v1.25h2.25a.25.25 0 0 0 .25-.25V1.75a.25.25 0 0 0-.25-.25h-8.5a.25.25 0 0 0-.25.25ZM3.75 6h.5a.75.75 0 0 1 0 1.5h-.5a.75.75 0 0 1 0-1.5M3 3.75A.75.75 0 0 1 3.75 3h.5a.75.75 0 0 1 0 1.5h-.5A.75.75 0 0 1 3 3.75m4 3A.75.75 0 0 1 7.75 6h.5a.75.75 0 0 1 0 1.5h-.5A.75.75 0 0 1 7 6.75M7.75 3h.5a.75.75 0 0 1 0 1.5h-.5a.75.75 0 0 1 0-1.5M3 9.75A.75.75 0 0 1 3.75 9h.5a.75.75 0 0 1 0 1.5h-.5A.75.75 0 0 1 3 9.75M7.75 9h.5a.75.75 0 0 1 0 1.5h-.5a.75.75 0 0 1 0-1.5"
							/>
						</svg>
						<p style={{ fontSize: "0.8rem", fontWeight: "500" }}>
							{organization.name}
						</p>
						{organizationIdSelected === organization.id ? (
							<svg
								style={{
									height: "1rem",
									width: "1rem",
									marginLeft: "auto",
									color: "#5271FF",
								}}
								viewBox="0 0 24 24"
							>
								<path
									fill="currentColor"
									d="M12 17q2.075 0 3.538-1.463T17 12t-1.463-3.537T12 7T8.463 8.463T7 12t1.463 3.538T12 17m0 5q-2.075 0-3.9-.788t-3.175-2.137T2.788 15.9T2 12t.788-3.9t2.137-3.175T8.1 2.788T12 2t3.9.788t3.175 2.137T21.213 8.1T22 12t-.788 3.9t-2.137 3.175t-3.175 2.138T12 22m0-2q3.35 0 5.675-2.325T20 12t-2.325-5.675T12 4T6.325 6.325T4 12t2.325 5.675T12 20"
								/>
							</svg>
						) : (
							<svg
								style={{
									height: "1rem",
									width: "1rem",
									marginLeft: "auto",
									color: "#999",
								}}
								viewBox="0 0 24 24"
							>
								<path
									fill="currentColor"
									d="M12 22q-2.075 0-3.9-.788t-3.175-2.137T2.788 15.9T2 12t.788-3.9t2.137-3.175T8.1 2.788T12 2t3.9.788t3.175 2.137T21.213 8.1T22 12t-.788 3.9t-2.137 3.175t-3.175 2.138T12 22m0-2q3.35 0 5.675-2.325T20 12t-2.325-5.675T12 4T6.325 6.325T4 12t2.325 5.675T12 20"
								/>
							</svg>
						)}
					</div>
				))}
			</div>

			<CoolButton
				fontSize="0.8rem"
				innerPadding={"0.5rem 2rem"}
				colored={true}
				onClick={onConfirm}
			>
				<p
					style={{
						fontSize: "0.75rem",
						fontWeight: "500",
						color: "#fff",
					}}
				>
					{t("authentication.confirm")}
				</p>
			</CoolButton>
		</div>
	)
}

const ShopSelector = ({
	shops,
	selectedShopUrl,
	onShopSelect,
	onConfirm,
	loading,
}) => {
	const { t } = useTranslation()

	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
			}}
		>
			<p
				style={{
					textAlign: "center",
					fontSize: "1.2rem",
					fontWeight: "700",
					color: "#333",
				}}
			>
				{t("authentication.select_your_store")}
			</p>
			<p
				style={{
					textAlign: "center",
					fontSize: "0.7rem",
					fontWeight: "500",
					color: "#555",
					marginBottom: "1rem",
				}}
			>
				{t("authentication.multiple_stores_found")}
			</p>
			<div style={{ marginBottom: "0.5rem" }}>
				{shops.map((shop) => (
					<div
						key={shop.shopUrl}
						className="shadow border"
						style={{
							padding: "0.3rem 0.8rem",
							borderRadius: "0.5rem",
							background:
								"linear-gradient(to right bottom, #eee 0%, #fff 90%)",
							color: "#444",
							marginBottom: "0.5rem",
							display: "flex",
							alignItems: "center",
							gap: "0.5rem",
							cursor: "pointer",
							minWidth: "12rem",
						}}
						onClick={() => onShopSelect(shop.shopUrl)}
					>
						<svg
							style={{ height: "0.8rem", width: "0.8rem" }}
							viewBox="0 0 24 24"
						>
							<path
								fill="currentColor"
								d="M4 7h16v2H4V7m0 6h16v-2H4v2m0 4h16v-2H4v2"
							/>
						</svg>
						<p style={{ fontSize: "0.8rem", fontWeight: "500" }}>
							{shop.shopUrl}
						</p>
						{selectedShopUrl === shop.shopUrl ? (
							<svg
								style={{
									height: "1rem",
									width: "1rem",
									marginLeft: "auto",
									color: "#5271FF",
								}}
								viewBox="0 0 24 24"
							>
								<path
									fill="currentColor"
									d="M12 17q2.075 0 3.538-1.463T17 12t-1.463-3.537T12 7T8.463 8.463T7 12t1.463 3.538T12 17m0 5q-2.075 0-3.9-.788t-3.175-2.137T2.788 15.9T2 12t.788-3.9t2.137-3.175T8.1 2.788T12 2t3.9.788t3.175 2.137T21.213 8.1T22 12t-.788 3.9t-2.137 3.175t-3.175 2.138T12 22m0-2q3.35 0 5.675-2.325T20 12t-2.325-5.675T12 4T6.325 6.325T4 12t2.325 5.675T12 20"
								/>
							</svg>
						) : (
							<svg
								style={{
									height: "1rem",
									width: "1rem",
									marginLeft: "auto",
									color: "#999",
								}}
								viewBox="0 0 24 24"
							>
								<path
									fill="currentColor"
									d="M12 22q-2.075 0-3.9-.788t-3.175-2.137T2.788 15.9T2 12t.788-3.9t2.137-3.175T8.1 2.788T12 2t3.9.788t3.175 2.137T21.213 8.1T22 12t-.788 3.9t-2.137 3.175t-3.175 2.138T12 22m0-2q3.35 0 5.675-2.325T20 12t-2.325-5.675T12 4T6.325 6.325T4 12t2.325 5.675T12 20"
								/>
							</svg>
						)}
					</div>
				))}
			</div>

			<CoolButton
				fontSize="0.8rem"
				innerPadding={"0.5rem 2rem"}
				colored={true}
				onClick={onConfirm}
				disabled={!selectedShopUrl || loading}
			>
				<p
					style={{
						fontSize: "0.75rem",
						fontWeight: "500",
						color: "#fff",
					}}
				>
					{loading
						? t("authentication.selecting")
						: t("authentication.confirm")}
				</p>
			</CoolButton>
		</div>
	)
}

const Step2 = ({ installTarget }) => {
	const { t } = useTranslation()
	const authenticatedFetch = useAuthenticatedFetch()
	const [opening, setOpening] = useState(false)
	const [error, setError] = useState(null)
	const openingRef = useRef(false)
	const store = installTarget?.store || ""

	// Each click asks the server for a fresh single-use state, so a replayed
	// click (double click, back button) can never resend a consumed one. The
	// ref guards the second click of a double click before React re-renders.
	const install = async () => {
		if (openingRef.current || !installTarget?.appId || !store) return
		openingRef.current = true
		setOpening(true)
		setError(null)

		const params = new URLSearchParams({
			store,
			appId: installTarget.appId,
			activeSubscriptionId: installTarget.activeSubscriptionId || "",
		})
		const appInstallUrl = await authenticatedFetch(
			`/api/auth/getInstallUrl?${params.toString()}`,
		)
			.then((res) => res.json())
			.then((data) => data?.appInstallUrl || null)
			.catch((err) => {
				console.error(err)
				return null
			})

		if (!appInstallUrl) {
			openingRef.current = false
			setOpening(false)
			posthog?.capture("store_install_url_failed", { store })
			setError(t("authentication.connect_error_link_failed"))
			return
		}
		posthog?.capture("store_install_clicked", { store })

		window.location.href = appInstallUrl
	}

	useEffect(() => {
		// Restored from the back/forward cache: the previous navigation is over.
		const onPageShow = (event) => {
			if (!event.persisted) return
			openingRef.current = false
			setOpening(false)
		}
		window.addEventListener("pageshow", onPageShow)
		return () => window.removeEventListener("pageshow", onPageShow)
	}, [])

	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
			}}
		>
			<CoolButton
				innerPadding={"0.5rem 1.5rem"}
				fontSize="0.8rem"
				colored={true}
				disabled={opening}
				onClick={install}
			>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						gap: "0.3rem",
					}}
				>
					{opening ? (
						<Loader size="0.8rem" color="#fff" />
					) : (
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="0.8rem"
							height="0.8rem"
							color="#fff"
							viewBox="0 0 16 16"
						>
							<path
								fill="currentColor"
								d="M12.25 1a.75.75 0 0 1 .75.75V3h1.25a.75.75 0 0 1 0 1.5H13v1.25a.75.75 0 0 1-1.5 0V4.5h-1.25a.75.75 0 0 1 0-1.5h1.25V1.75a.75.75 0 0 1 .75-.75m-8.5 2.5H7a.25.25 0 0 1 .25.25v3.5H3.5v-3.5a.25.25 0 0 1 .25-.25m5 3.75v-3.5A1.75 1.75 0 0 0 7 2H3.75A1.75 1.75 0 0 0 2 3.75v8.5c0 .966.784 1.75 1.75 1.75h8.5A1.75 1.75 0 0 0 14 12.25V9a1.75 1.75 0 0 0-1.75-1.75zm-1.5 1.5v3.75h-3.5a.25.25 0 0 1-.25-.25v-3.5zm1.5 3.75V8.75h3.5a.25.25 0 0 1 .25.25v3.25a.25.25 0 0 1-.25.25z"
							/>
						</svg>
					)}
					<p
						style={{
							fontSize: "0.75rem",
							fontWeight: "500",
							color: "#fff",
						}}
					>
						{opening
							? t("authentication.opening_shopify")
							: t("authentication.install_kopy")}
					</p>
				</div>
			</CoolButton>

			<div style={{ marginTop: "0.5rem" }}>
				<p
					style={{
						textAlign: "center",
						fontSize: "0.6rem",
						fontWeight: "500",
						color: "#555",
					}}
				>
					{t("authentication.the_app_will_be_installed_to")}
				</p>
				<p
					style={{
						textAlign: "center",
						fontSize: "0.7rem",
						fontWeight: "700",
						color: "#333",
					}}
				>
					{store}
				</p>
			</div>

			{error ? (
				<div
					style={{
						marginTop: "0.5rem",
						color: "red",
						fontSize: "0.8rem",
						fontWeight: "500",
					}}
				>
					{error}
				</div>
			) : (
				""
			)}
		</div>
	)
}

export default Authentication
